import { BrowserDataCacheSourceAdapter } from '../core/source-adapter';
import { BrowserDataCacheRuntime, BrowserDataCacheRuntimeCandidate, BrowserDataCacheRuntimeProbeResult } from '../core/runtime';
import {
  BrowserDataCacheCapabilities,
  BrowserDataCacheQuery,
  BrowserDataCacheQueryResult,
  BrowserDataCacheSyncJob,
  BrowserDataCacheSyncOptions,
} from '../core/types';
import { BrowserDataCacheSqlDriver, BrowserDataCacheSqlDriverFactory } from '../sqlite/driver';
import { BrowserDataCacheSqlSchema, buildSqlPlan } from '../sqlite/schema';
import { BrowserDataCacheTableMapping, buildSelectStatement, buildUpsertStatement } from '../sqlite/table-mapping';
import { BrowserDataCacheSubject } from '../core/subject';
import { collectBrowserDataCachePages } from '../core/source-helpers';

export interface SqliteRuntimeOptions {
  id: 'worker-opfs-sqlite' | 'main-opfs-sqlite';
  sources: BrowserDataCacheSourceAdapter<any, any, any>[];
  schema: BrowserDataCacheSqlSchema;
  tableMappings?: BrowserDataCacheTableMapping<any>[];
  driverFactory: BrowserDataCacheSqlDriverFactory;
  probe?: () => Promise<BrowserDataCacheRuntimeProbeResult> | BrowserDataCacheRuntimeProbeResult;
}

export class SqliteRuntime implements BrowserDataCacheRuntime {
  readonly capabilities: BrowserDataCacheCapabilities;

  private driver: BrowserDataCacheSqlDriver | null = null;
  private initPromise: Promise<void> | null = null;
  private readonly sources = new Map<string, BrowserDataCacheSourceAdapter<any, any, any>>();
  private readonly mappings = new Map<string, BrowserDataCacheTableMapping<any>>();

  constructor(private readonly options: SqliteRuntimeOptions) {
    this.capabilities = {
      runtime: options.id,
      persistent: true,
      sql: true,
      views: true,
      materializedViews: true,
      backgroundWorker: options.id === 'worker-opfs-sqlite',
    };
    for (const source of options.sources || []) {
      this.sources.set(source.id, source);
    }
    for (const mapping of options.tableMappings || []) {
      this.mappings.set(mapping.table, mapping);
    }
  }

  async ensureReady(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this.init();
    return this.initPromise;
  }

  startSync(sourceId: string, options: BrowserDataCacheSyncOptions = {}): BrowserDataCacheSyncJob {
    const source = this.getSource(sourceId);
    const mapping = this.getMapping(source.table);
    const progress = new BrowserDataCacheSubject<any>();
    const controller = new AbortController();
    const signal = mergeAbortSignals(controller.signal, options.signal);
    const done = this.runSync(source, mapping, options, signal, progress);
    return {
      id: `sqlite-sync-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      sourceId,
      progress$: progress,
      done,
      cancel: () => controller.abort(),
    };
  }

  async query<TRow = unknown>(query: BrowserDataCacheQuery): Promise<BrowserDataCacheQueryResult<TRow>> {
    await this.ensureReady();
    const driver = this.requireDriver();
    const table = query.table || query.view || this.getSource(query.sourceId || '')?.table;
    const mapping = this.getMapping(table || '');
    const statement = buildSelectStatement(mapping, query);
    const rows = await driver.query(statement.sql, statement.params);
    return {
      rows: rows as TRow[],
      total: rows.length,
      capabilities: this.capabilities,
    };
  }

  async upsertRows(table: string, rows: unknown[]): Promise<void> {
    await this.ensureReady();
    const driver = this.requireDriver();
    const mapping = this.getMapping(table);
    await driver.transaction(async () => {
      for (const row of rows) {
        const statement = buildUpsertStatement(mapping, row);
        await driver.execute(statement.sql, statement.params);
      }
    });
    await driver.flush?.();
  }

  async close(): Promise<void> {
    await this.driver?.close();
    this.driver = null;
    this.initPromise = null;
  }

  private async init(): Promise<void> {
    this.driver = await this.options.driverFactory.create();
    const plan = buildSqlPlan(this.options.schema);
    await this.driver.transaction(async () => {
      for (const sql of plan.createSql) {
        await this.driver?.execute(sql);
      }
      for (const migration of plan.migrations) {
        const statements = Array.isArray(migration.sql) ? migration.sql : [migration.sql];
        for (const sql of statements) {
          await this.driver?.execute(sql);
        }
      }
      for (const sql of plan.viewSql) {
        await this.driver?.execute(sql);
      }
      for (const sql of plan.refreshMaterializedViewSql) {
        await this.driver?.execute(sql);
      }
    });
    await this.driver.flush?.();
  }

  private async runSync(
    source: BrowserDataCacheSourceAdapter<any, any, any>,
    mapping: BrowserDataCacheTableMapping<any>,
    options: BrowserDataCacheSyncOptions,
    signal: AbortSignal,
    progress: BrowserDataCacheSubject<any>,
  ) {
    await this.ensureReady();
    const driver = this.requireDriver();
    progress.next({ sourceId: source.id, phase: 'fetching', statusText: 'Fetching pages' });
    const pages = await collectBrowserDataCachePages(
      (context, cursor) => source.listPage(context, cursor),
      {
        signal,
        stopWhen: options.stopWhen,
        getModifiedAt: source.getModifiedAt,
      },
    );
    progress.next({ sourceId: source.id, phase: 'mapping', completed: pages.items.length, total: pages.items.length });
    const rows = pages.items.map((item) => source.mapRow(item)).filter(Boolean);
    await driver.transaction(async () => {
      for (const row of rows) {
        const statement = buildUpsertStatement(mapping, row);
        await driver.execute(statement.sql, statement.params);
      }
      for (const sql of buildSqlPlan(this.options.schema).refreshMaterializedViewSql) {
        await driver.execute(sql);
      }
    });
    await driver.flush?.();
    progress.next({ sourceId: source.id, phase: 'completed', completed: rows.length, total: rows.length });
    return {
      sourceId: source.id,
      rowsRead: pages.items.length,
      rowsWritten: rows.length,
      cancelled: false,
    };
  }

  private requireDriver(): BrowserDataCacheSqlDriver {
    if (!this.driver) {
      throw new Error('SQLite driver is not ready.');
    }
    return this.driver;
  }

  private getSource(sourceId: string): BrowserDataCacheSourceAdapter<any, any, any> {
    const source = this.sources.get(sourceId);
    if (!source) {
      throw new Error(`Unknown browser data cache source: ${sourceId}`);
    }
    return source;
  }

  private getMapping(table: string): BrowserDataCacheTableMapping<any> {
    const mapping = this.mappings.get(table);
    if (!mapping) {
      throw new Error(`No table mapping registered for ${table}`);
    }
    return mapping;
  }
}

function mergeAbortSignals(primary: AbortSignal, secondary?: AbortSignal): AbortSignal {
  if (!secondary) return primary;
  if (secondary.aborted) {
    const controller = new AbortController();
    controller.abort(secondary.reason);
    return controller.signal;
  }
  const controller = new AbortController();
  const abort = () => controller.abort(primary.reason || secondary.reason);
  primary.addEventListener('abort', abort, { once: true });
  secondary.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

export function createSqliteRuntimeCandidate(options: SqliteRuntimeOptions): BrowserDataCacheRuntimeCandidate {
  return {
    id: options.id,
    probe: options.probe || (() => ({ ok: true })),
    create: async () => {
      const runtime = new SqliteRuntime(options);
      await runtime.ensureReady();
      return runtime;
    },
  };
}
